import { useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ShipmentType } from "Util/enums";

import { NotificationToast } from "../../../../../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../../../../../constants";
import { useAppState } from "../../../../../context/StateContext";
import { ContainerSize } from "../../../../../models/container.size";
import { Error } from "../../../../../models/error";
import { Filters, Searches, Sorts, Sort } from "../../../../../models/shipeezi.table";
import { ShipmentTrackingToExport } from "../../../../../models/shipment.tracking";
import { ISubscriber, Shipment } from "../../../../../models/shipments";
import { Waybill } from "../../../../../models/waybill";
import { WaybillDocument } from "../../../../../models/waybill.document";
import ShipmentDocumentService from "../../../../../services/shipment.document.service";
import { ShipmentTrackingVersionService } from "../../../../../services/shipment.tracking.service";
import WaybillsService from "../../../../../services/waybill.service";
import { checkError } from "../../../../../util/checkError";
import getRelatedFilters from "../../../../../util/getRelatedFilters";
import { handleSort } from "../../../../../util/handleSort";
import { GLOBAL_STATE_SHIPMENT } from "../constants/Waybills.const";
import { ColumnFilter } from "Components/AppGrid/shipeezi-new-grid";

const DEFAULT_PAGESIZE_LIST = [20, 40, 60, 100];
interface UpdateProps {
  id: number;
  waybill: Shipment;
}

export const INITIAL_PAGESIZE: number = 20;
export const useActions = () => {
  const { currentUser, currentCustomerUser } = useAppState();
  const { t: translate } = useTranslation();
  const history = useHistory();

  const [waybills, setWaybills] = useState<Shipment[]>([]);

  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [containerId, setContainerId] = useState<string>("");
  const [billOfLanding, setBillOfLanding] = useState<string>("");
  const [searches, setSearches] = useState<Searches>([]);
  const [sorting, setSorting] = useState<Sorts>();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(INITIAL_PAGESIZE);
  const [pageSizeList, setPageSizeList] = useState<number[]>(DEFAULT_PAGESIZE_LIST);
  const [selectedStatus, setSelectedStatus] = useState<any[]>([]);
  const [selectedShipmentType, setSelectedShipmentType] = useState<ShipmentType>(ShipmentType.SEA);
  const [selectedRowList, setSelectedRowList] = useState<number[]>([]);
  const [isOpenModalPreference, setIsOpenModalPreference] = useState<boolean>(false);
  const [isOpenDialogExportCsv, setIsOpenDialogExportCsv] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchOptions, setSearchOptions] = useState<ColumnFilter[]>([]);
  const [columnFieldSeaModalPreference, setColumnFieldSeaModalPreference]: any = useState<any>([
    {
      code: "containerId",
      label: translate("reference"),
      isChecked: true,
    },
    {
      code: "loadType",
      label: translate("loadType"),
      isChecked: true,
    },
    {
      code: "billOfLanding",
      label: translate("bolBooking"),
      isChecked: true,
    },
    {
      code: "jobs",
      label: translate("order"),
      isChecked: true,
    },
    {
      code: "pol",
      label: translate("pol"),
      isChecked: true,
    },
    {
      code: "pod",
      label: translate("pod"),
      isChecked: true,
    },
    {
      code: "etd",
      label: translate("etd"),
      isChecked: true,
    },
    {
      code: "eta",
      label: translate("eta"),
      isChecked: true,
    },
    {
      code: "cargoReadyDate",
      label: translate("loadBy"),
      isChecked: true,
    },
    {
      code: "delayed",
      label: translate("delayed"),
      isChecked: true,
    },
    {
      code: "utilization",
      label: translate("utilization"),
      isChecked: true,
    },
    {
      code: "statusShipment",
      label: translate("status"),
      isChecked: true,
    },
  ]);

  const [columnFieldAirModalPreference, setColumnFieldAirModalPreference]: any = useState<any>([
    {
      code: "containerId",
      label: translate("reference"),
      isChecked: true,
    },
    {
      code: "loadType",
      label: translate("loadType"),
      isChecked: true,
    },
    {
      code: "billOfLanding",
      label: translate("bolBooking"),
      isChecked: true,
    },
    {
      code: "jobs",
      label: translate("order"),
      isChecked: true,
    },
    { code: "origin", label: translate("originAirport"), isChecked: true },
    { code: "destination", label: translate("destinationAirport"), isChecked: true },
    {
      code: "etd",
      label: translate("etd"),
      isChecked: true,
    },
    {
      code: "eta",
      label: translate("eta"),
      isChecked: true,
    },
    {
      code: "cargoReadyDate",
      label: translate("loadBy"),
      isChecked: true,
    },
    {
      code: "delayed",
      label: translate("delayed"),
      isChecked: true,
    },
    {
      code: "utilization",
      label: translate("utilization"),
      isChecked: true,
    },
    {
      code: "statusShipment",
      label: translate("status"),
      isChecked: true,
    },
  ]);

  const columnFieldModalPreference: any = {
    [ShipmentType.SEA]: columnFieldSeaModalPreference,
    [ShipmentType.AIR]: columnFieldAirModalPreference,
    [ShipmentType.ROAD]: null,
    [ShipmentType.RAIL]: null,
  };

  const useAddShipmentSubscriber = () => {
    return useMutation(["create.shipment.subscriber"], (data: ISubscriber) =>
      WaybillsService.addSubscriber(data)
    );
  };

  const { mutate: addSubscriber } = useAddShipmentSubscriber();

  const addSubscribers = (subscribers: ISubscriber[], shipmentId: string | number) => {
    subscribers?.map((subscriber) => {
      if (subscriber?.id) {
        delete subscriber.id;
      }

      return addSubscriber({ ...subscriber, shipmentId: Number(shipmentId) });
    });
  };
  const getWaybillList = () =>
    useQuery(
      ["waybill.list", page, pageSize, searches, sorting, filters, billOfLanding, containerId],
      () => {
        const param: any = {
          page,
          pageSize,
          sortBy:
            sorting && sorting?.length > 0
              ? JSON.stringify(sorting)
              : JSON.stringify([["updatedAt", "desc"]]),
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify([
            {
              association: "jobs",
              include: [{ association: "itemsJob" }, { association: "customer" }],
              required: false,
            },
            {
              association: "customersToShipment",
              required: false,
            },
            { association: "milestones", include: [{ association: "milestone" }] },
            { association: "shipmentTracking", required: false },
            {
              association: "containers",
              where: Object.values(filters ?? {}).length
                ? getRelatedFilters(filters, "containers")
                : null,
            },
          ]),
          billOfLanding: billOfLanding,
          container_id: containerId,
          ...getRelatedFilters(filters, "shipment"),
        };

        if (!billOfLanding || billOfLanding === "") {
          delete param?.billOfLanding;
        }

        if (!containerId || containerId === "") {
          delete param?.container_id;
        }

        return WaybillsService.getAll(param);
      },
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) setWaybills(response.data);
        },
      }
    );

  const getWaybillCount = () =>
    useQuery(
      ["waybills.count", filters, searches, billOfLanding, containerId],
      () => {
        const param: any = {
          companyId: currentUser?.companyId,
          searchByOr: searches && searches.length > 0 ? JSON.stringify(searches) : "",
          include: JSON.stringify([
            { association: "shipmentTracking" },
            {
              association: "containers",
              where: Object.values(filters ?? {}).length
                ? getRelatedFilters(filters, "containers")
                : null,
            },
          ]),
          billOfLanding: billOfLanding,
          container_id: containerId,
          ...getRelatedFilters(filters, "shipment"),
        };

        if (!billOfLanding || billOfLanding === "") {
          delete param?.billOfLanding;
        }

        if (!containerId || containerId === "") {
          delete param?.container_id;
        }

        return WaybillsService.count(param);
      },
      {
        enabled: !!currentUser?.companyId,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data > 0) {
            setTotal(response.data);
          } else {
            setTotal(0);
          }
        },
      }
    );

  const getContainerSizes = (setContainerSize: (containerSizes: ContainerSize[]) => void) =>
    useQuery(
      ["container.size.list"],
      () =>
        WaybillsService.getContainerSizes({
          sortBy: JSON.stringify([["label", "ASC"]]),
        }),
      {
        enabled: !!currentUser?.id || !!currentCustomerUser?.id,
        onSuccess: (response) => {
          if (response && response.data) setContainerSize(response.data);
        },
      }
    );

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleBillOfLanding = (billOfLanding: string) => {
    setBillOfLanding(billOfLanding);
    setPage(0);
  };

  const handleContainerId = (containerId: string) => {
    setContainerId(containerId);
    setPage(0);
  };

  const handleFilter = (values: Filters) => {
    setFilters(values);
    setPage(0);
  };

  const handleSorting = (sorts: Sort[]) => {
    setSorting(handleSort(sorts));
  };

  const handleCurrentPage = (page: number) => setPage(page - 1);

  const handleFilterTypeShipment = (type: ShipmentType) => {
    setSelectedShipmentType(type);
    const payload = {
      "shipment.shipmentType": [type],
    } as any;

    onResetFilterAfterChangeShipmentType(type);

    // if (selectedStatus.length > 0) {
    //   payload["shipment.statusShipment"] = selectedStatus;
    // }

    setFilters(payload);
  };

  const handleFilterStatusShipment = (statusList: any[]) => {
    const payload = {
      "shipment.statusShipment": statusList,
    } as any;

    if (selectedShipmentType) {
      payload["shipment.shipmentType"] = selectedShipmentType;
    }

    setSelectedStatus(statusList);
    setFilters(payload);
    setPage(0);
  };

  const useUpdateShipmentSubscriber = () => {
    return useMutation(
      ["update.shipment.subscriber"],
      (params: { subscriberId: string | number; subscriber: ISubscriber }) =>
        WaybillsService.updateSubscriber(Number(params?.subscriberId), params.subscriber)
    );
  };

  const useDeleteShipmentSubscriber = () => {
    return useMutation(["delete.shipment.subscriber"], (subscriberId: string) =>
      WaybillsService.deleteSubscriber(Number(subscriberId))
    );
  };

  const useCreateWaybill = () => {
    return useMutation(
      ["waybill.create"],
      (waybill: Shipment) => {
        return WaybillsService.create({ ...waybill, companyId: currentUser?.companyId });
      },
      {
        onSuccess: (response, variables) => {
          const subscribers = variables?.subscribers?.filter((i) => i?.isNew);
          const isDuringCreation = !variables?.id;
          const shipmentData = response?.data;

          if (isDuringCreation && !_.isEmpty(subscribers)) {
            addSubscribers(subscribers!, shipmentData?.id!);
          }
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          history.push(PRIVATE_ROUTES.SHIPMENTS);
        },
        onError: (err: AxiosError<Error>) => {
          checkError(err, translate("anErrorOccurredTryAgain"));
        },
      }
    );
  };

  const useUpdateWaybill = () => {
    return useMutation(
      ["waybill.update"],
      ({ id, waybill }: UpdateProps) => {
        return WaybillsService.update(id, waybill);
      },
      {
        onSuccess: (response, variables) => {
          const subscribers = variables?.waybill?.subscribers?.filter((i) => i?.isNew);
          const shipmentId = variables?.waybill?.id;
          const isDuringCreation = !variables?.id;

          if (!_.isEmpty(subscribers) && isDuringCreation) {
            addSubscribers(subscribers!, shipmentId!);
          }
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          history.push(PRIVATE_ROUTES.SHIPMENTS);
        },
        onError: (err: AxiosError<Error>) => {
          checkError(err, translate("anErrorOccurredTryAgain"));
        },
      }
    );
  };

  const { mutate, isLoading: isSubscribing } = useMutation(
    ["subscribing"],
    ({ id, waybill }: UpdateProps) => WaybillsService.subscribe(id, waybill),
    {
      onSuccess: () => {
        NotificationToast.success(translate("operationPerformedSuccessfully"));
        history.push(PRIVATE_ROUTES.SHIPMENTS);
      },
      onError: (err: AxiosError<Error>) => {
        checkError(err, translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const useCreateWaybillAndSubscribe = () => {
    return useMutation(
      ["create.waybill"],
      (waybill: Waybill) => {
        return WaybillsService.create({ ...waybill, companyId: currentUser?.companyId });
      },
      {
        onSuccess: (response, variables) => {
          const shipmentData = response?.data;
          const subscribers = variables?.subscribers?.filter((i) => i?.isNew);
          const isDuringCreation = !variables?.id;

          if (shipmentData && isDuringCreation) {
            NotificationToast.success(translate("operationPerformedSuccessfully"));
            if (!_.isEmpty(subscribers)) {
              addSubscribers(subscribers!, shipmentData?.id!);
            }

            history.push(PRIVATE_ROUTES.SHIPMENTS);
          }
        },
      }
    );
  };

  const useUploadWaybillDocument = (callback: (waybillDocument: WaybillDocument) => void) =>
    useMutation(
      ["upload.file"],
      (waybillDocument: WaybillDocument) => ShipmentDocumentService.create(waybillDocument),
      {
        onSuccess: (response) => {
          NotificationToast.success(translate("operationPerformedSuccessfully"));
          callback(response.data);
        },
      }
    );

  const getDataToExport = (callback: (data: ShipmentTrackingToExport[]) => void) =>
    useQuery(
      ["export.csv"],
      () =>
        ShipmentTrackingVersionService.getDataToExport({
          companyId: currentUser?.companyId,
        }),
      {
        enabled: false,
        refetchOnWindowFocus: false,
        onSuccess: (response) => {
          if (response && response.data) callback(response.data);
        },
      }
    );

  const onResetFilterAfterChangeShipmentType = (type: string) => {
    setSelectedRowList([]);
    setPageSize(INITIAL_PAGESIZE);
    handleCurrentPage(1);
    setSelectedStatus([]);
    setSorting([]);
    setSearchInput("");
    setSearchOptions([]);
    setSearches([]);
    setBillOfLanding("");
    setContainerId("");
  };

  const onResetFormPreference = (resetFieldColumn: any) => {
    if (selectedShipmentType === ShipmentType.SEA) {
      setColumnFieldSeaModalPreference(resetFieldColumn);
    }
    if (selectedShipmentType === ShipmentType.AIR) {
      setColumnFieldAirModalPreference(resetFieldColumn);
    }
  };

  const onSubmitFormPreference = (dataModal: any) => {
    localStorage.setItem(
      GLOBAL_STATE_SHIPMENT[selectedShipmentType],
      JSON.stringify(dataModal?.newDataColumn)
    );
    if (selectedShipmentType === ShipmentType.SEA) {
      setColumnFieldSeaModalPreference(dataModal?.newDataColumn);
      // set show and hide column of grid
    } else if (selectedShipmentType === ShipmentType.AIR) {
      setColumnFieldAirModalPreference(dataModal?.newDataColumn);
      // set show and hide column of grid
    }
    setIsOpenModalPreference(false);
    setPageSize(dataModal.pageSize);
    const findPageSize = pageSizeList.find((item: number) => item === dataModal.pageSize);
    // check if dataModal.pageSize is not in pageSizeList will push data in pageSizeList and set new page size

    if (!findPageSize) {
      const updatedList = Array.from(new Set([...DEFAULT_PAGESIZE_LIST, dataModal.pageSize])).sort(
        (a, b) => a - b
      );
      setPageSizeList(updatedList);
    }
  };

  return {
    waybills,
    total,
    page,
    pageSize,
    isSubscribing,
    selectedStatus,
    selectedShipmentType,
    setSelectedShipmentType,
    selectedRowList,
    isOpenModalPreference,
    isOpenDialogExportCsv,
    columnFieldModalPreference,
    pageSizeList,
    searchInput,
    searchOptions,

    setSearchInput,
    setSearchOptions,
    setPageSizeList,
    setIsOpenModalPreference,
    setSelectedRowList,
    setPageSize,
    setIsOpenDialogExportCsv,
    getWaybillCount,
    getWaybillList,
    getContainerSizes,
    handleSearch,
    handleBillOfLanding,
    handleContainerId,
    handleFilter,
    handleSorting,
    handleCurrentPage,
    handleFilterTypeShipment,
    handleFilterStatusShipment,
    useCreateWaybill,
    useUpdateWaybill,
    useSubscribeWaybill: mutate,
    useCreateWaybillAndSubscribe,
    useUpdateShipmentSubscriber,
    useDeleteShipmentSubscriber,
    useAddShipmentSubscriber,
    useUploadWaybillDocument,
    getDataToExport,
    onSubmitFormPreference,
    onResetFormPreference,
    setColumnFieldSeaModalPreference,
    setColumnFieldAirModalPreference,
  };
};
